import { useEffect, useRef, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { LEAGUES } from '../../utils/constants';
import { ScreenerAutocomplete } from './ScreenerAutocomplete';
import { ScreenerMarketsDropdown } from './ScreenerMarketsDropdown';
import {
	Button,
	Checkbox,
	CircularProgress,
	InputAdornment,
	InputBase,
	List,
	ListItemButton,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { addPlusSign } from '../evscreen/EvScreenTable/evScreenTable';
import { SelectionPopover } from './SelectionPopover';
import { LogoGenerator } from '../../components/LogoGenerator';
import { BetActionsOverlay } from './BetActionComponent';
import { LEAGUE_SPORT_MAP } from './leagueToSportMapping';
import { SPORT_TO_MARKET_MAP, SPORT_TO_POPULAR_MARKETS } from './sportToMarketMapping';
import { IOSSwitch } from '../../components/IOSSwitch';
import { ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import './Screener.css';
import { useInfiniteOddsScreen } from '../../hooks/useInfiniteOddsScreen';
import { ODDS_SCREEN_PAGE_SIZE } from '../../api/oddsScreenApi';
import { useOddsStream } from '../../hooks/useOddsStream';
import { getSportsbookDisplayName, mergePricesByDisplaySportsbook, normalizeSportsbookList } from '../../utils/sportsbookAliases';

/** Backend sends American odds; format for display (round, no conversion) */
function formatAmerican(american) {
	if (american == null || american === '' || isNaN(Number(american))) return null;
	return Math.round(Number(american));
}

/** Spread API stores juice in *_positive or *_negative depending on line sign, not bet_price_over/under. */
function spreadAmericanFromPriceRow(row, side, spreadNum) {
	if (!row || spreadNum == null || isNaN(spreadNum)) return null;
	const prefix = side === 'under' ? 'under' : 'over';
	let pos = row[`bet_price_${prefix}_positive`];
	let neg = row[`bet_price_${prefix}_negative`];
	let plain = row[`bet_price_${prefix}`];
	// Unmerged /odds_screen rows only populate bet_price_over_* on each outcome
	if (pos == null && neg == null && plain == null) {
		pos = row.bet_price_over_positive;
		neg = row.bet_price_over_negative;
		plain = row.bet_price_over;
	}
	if (spreadNum > 0) return formatAmerican(pos ?? plain ?? neg);
	if (spreadNum < 0) return formatAmerican(neg ?? plain ?? pos);
	return formatAmerican(plain ?? pos ?? neg);
}

function spreadHighestFromStats(stats, side, spreadNum) {
	if (!stats || spreadNum == null || isNaN(spreadNum)) return null;
	const prefix = side === 'under' ? 'under' : 'over';
	if (spreadNum > 0) {
		return formatAmerican(
			stats[`highest_bet_price_${prefix}_positive`] ?? stats.highest_bet_price_over_positive,
		);
	}
	if (spreadNum < 0) {
		return formatAmerican(
			stats[`highest_bet_price_${prefix}_negative`] ?? stats.highest_bet_price_over_negative,
		);
	}
	return formatAmerican(
		stats[`highest_bet_price_${prefix}_positive`] ??
			stats[`highest_bet_price_${prefix}_negative`] ??
			stats.highest_bet_price_over_positive ??
			stats.highest_bet_price_over_negative,
	);
}

function spreadAverageFromStats(stats, side, spreadNum) {
	if (!stats || spreadNum == null || isNaN(spreadNum)) return null;
	const prefix = side === 'under' ? 'under' : 'over';
	if (spreadNum > 0) {
		return formatAmerican(
			stats[`average_bet_price_${prefix}_positive`] ?? stats.average_bet_price_over_positive,
		);
	}
	if (spreadNum < 0) {
		return formatAmerican(
			stats[`average_bet_price_${prefix}_negative`] ?? stats.average_bet_price_over_negative,
		);
	}
	return formatAmerican(
		stats[`average_bet_price_${prefix}_positive`] ??
			stats[`average_bet_price_${prefix}_negative`] ??
			stats.average_bet_price_over_positive ??
			stats.average_bet_price_over_negative,
	);
}

function spreadBestBookFromStats(stats, side, spreadNum) {
	if (!stats || spreadNum == null || isNaN(spreadNum)) return null;
	const prefix = side === 'under' ? 'under' : 'over';
	if (spreadNum > 0) return stats[`best_sportsbook_${prefix}_positive`];
	if (spreadNum < 0) return stats[`best_sportsbook_${prefix}_negative`];
	return stats[`best_sportsbook_${prefix}_positive`] ?? stats[`best_sportsbook_${prefix}_negative`];
}

/** Placeholder when a book has no odds for that side of the bet (avoids empty box looking like a bug) */
const NO_ODDS_PLACEHOLDER = '—';

function replaceTeam(str, newTeam) {
	const parts = str.split(':');
	if (parts.length < 4) return str; // guard for malformed strings
	parts[2] = newTeam;
	return parts.join(':');
}
function toggleFinalNumberSign(str) {
	return str.replace(/:(-?\d+(\.\d+)?)$/, (match, num) => {
		const toggled = num.startsWith('-') ? num.slice(1) : '-' + num;
		return ':' + toggled;
	});
}
function toggleNegative(str) {
	if (str?.startsWith('-')) {
		return str.slice(1); // remove the "-"
	} else {
		return '-' + str; // add the "-"
	}
}

/** Normalize game fields: prefer API names (home_team_name, away_team_name, game_id), fallback to _display / id */
function getGameDisplay(game) {
	if (!game) return {};
	return {
		...game,
		home_team_display: game.home_team_name ?? game.home_team_display,
		away_team_display: game.away_team_name ?? game.away_team_display,
		id: game.game_id ?? game.id,
	};
}

/** Obsolete; exclude from screener entirely. */
const SCREENER_EXCLUDED_BOOKS = ['Betway (Alaska)'];
function isExcludedScreenerBook(book) {
	return !book || SCREENER_EXCLUDED_BOOKS.includes(book);
}

/** Parse search query into text (player/team) and optional line value. e.g. "cason wallace 2.5" -> { text: "cason wallace", line: 2.5 } */
function parseSearchQuery(query) {
	const trimmed = (query || '').trim().toLowerCase();
	if (!trimmed) return { text: '', line: null };
	const parts = trimmed.split(/\s+/);
	const last = parts[parts.length - 1];
	const lineNum = parseFloat(last);
	const isLine = last !== '' && !isNaN(lineNum) && String(lineNum) === String(Number(last));
	if (isLine && parts.length > 1) {
		return { text: parts.slice(0, -1).join(' ').trim(), line: lineNum };
	}
	if (isLine && parts.length === 1) {
		return { text: '', line: lineNum };
	}
	return { text: trimmed, line: null };
}

/** Resolve deep link: backend may send string or { desktop } */
function resolveDeepLink(link) {
	if (typeof link === 'string') return link;
	return link?.desktop ?? null;
}

function getHighlightClass(highlightedPrices, ...normIds) {
	for (const id of normIds) {
		if (id && highlightedPrices.has(id)) {
			return highlightedPrices.get(id) === 'up' ? 'price-up' : 'price-down';
		}
	}
	return '';
}

function americanToImpliedProb(american) {
	if (american == null || typeof american !== 'number') return null;
	if (american > 0) return 100 / (american + 100);
	return Math.abs(american) / (Math.abs(american) + 100);
}

function impliedProbToAmerican(prob) {
	if (prob == null || prob <= 0 || prob >= 1) return null;
	if (prob >= 0.5) return Math.round((-100 * prob) / (1 - prob));
	return Math.round((100 * (1 - prob)) / prob);
}

/** Average American odds by averaging implied probabilities, then convert back to American */
function averageAmericanFromOdds(americanOddsArray) {
	const valid = americanOddsArray.filter((o) => typeof o === 'number');
	if (!valid.length) return null;
	const probs = valid.map(americanToImpliedProb).filter((p) => p != null);
	if (!probs.length) return null;
	const avgProb = probs.reduce((a, b) => a + b, 0) / probs.length;
	return impliedProbToAmerican(avgProb);
}

function getBetPriceStats(data) {
	const overOdds = [];
	const underOdds = [];
	const overPositiveOdds = [];
	const overNegativeOdds = [];
	const underPositiveOdds = [];
	const underNegativeOdds = [];
	let maxOver = -Infinity;
	let maxUnder = -Infinity;
	let maxOverPositive = -Infinity;
	let maxUnderPositive = -Infinity;
	let maxOverNegative = -Infinity;
	let maxUnderNegative = -Infinity;
	let bestSportsbookOver = null;
	let bestSportsbookUnder = null;
	let bestSportsbookOverPositive = null;
	let bestSportsbookOverNegative = null;
	let bestSportsbookUnderPositive = null;
	let bestSportsbookUnderNegative = null;

	if (data?.length) {
		for (const item of data) {
			if (typeof item.bet_price_over === 'number') {
				overOdds.push(item.bet_price_over);
				if (item.bet_price_over > maxOver) {
					maxOver = item.bet_price_over;
					bestSportsbookOver = item.sportsbook;
				}
			}
			if (typeof item.bet_price_under === 'number') {
				underOdds.push(item.bet_price_under);
				if (item.bet_price_under > maxUnder) {
					maxUnder = item.bet_price_under;
					bestSportsbookUnder = item.sportsbook;
				}
			}
			if (typeof item.bet_price_over_positive === 'number') {
				overPositiveOdds.push(item.bet_price_over_positive);
				if (item.bet_price_over_positive > maxOverPositive) {
					maxOverPositive = item.bet_price_over_positive;
					bestSportsbookOverPositive = item.sportsbook;
				}
			}
			if (typeof item.bet_price_over_negative === 'number') {
				overNegativeOdds.push(item.bet_price_over_negative);
				if (item.bet_price_over_negative > maxOverNegative) {
					maxOverNegative = item.bet_price_over_negative;
					bestSportsbookOverNegative = item.sportsbook;
				}
			}
			if (typeof item.bet_price_under_positive === 'number') {
				underPositiveOdds.push(item.bet_price_under_positive);
				if (item.bet_price_under_positive > maxUnderPositive) {
					maxUnderPositive = item.bet_price_under_positive;
					bestSportsbookUnderPositive = item.sportsbook;
				}
			}
			if (typeof item.bet_price_under_negative === 'number') {
				underNegativeOdds.push(item.bet_price_under_negative);
				if (item.bet_price_under_negative > maxUnderNegative) {
					maxUnderNegative = item.bet_price_under_negative;
					bestSportsbookUnderNegative = item.sportsbook;
				}
			}
		}
	}

	return {
		highest_bet_price_over: overOdds.length ? maxOver : null,
		highest_bet_price_under: underOdds.length ? maxUnder : null,
		best_sportsbook_over: bestSportsbookOver,
		best_sportsbook_under: bestSportsbookUnder,
		average_bet_price_over: averageAmericanFromOdds(overOdds),
		average_bet_price_under: averageAmericanFromOdds(underOdds),

		highest_bet_price_over_positive: overPositiveOdds.length ? maxOverPositive : null,
		highest_bet_price_over_negative: overNegativeOdds.length ? maxOverNegative : null,
		highest_bet_price_under_positive: underPositiveOdds.length ? maxUnderPositive : null,
		highest_bet_price_under_negative: underNegativeOdds.length ? maxUnderNegative : null,
		best_sportsbook_over_positive: bestSportsbookOverPositive,
		best_sportsbook_over_negative: bestSportsbookOverNegative,
		best_sportsbook_under_positive: bestSportsbookUnderPositive,
		best_sportsbook_under_negative: bestSportsbookUnderNegative,
		average_bet_price_over_positive: averageAmericanFromOdds(overPositiveOdds),
		average_bet_price_over_negative: averageAmericanFromOdds(overNegativeOdds),
		average_bet_price_under_positive: averageAmericanFromOdds(underPositiveOdds),
		average_bet_price_under_negative: averageAmericanFromOdds(underNegativeOdds),
	};
}

/** Compact date for table: "2/22 • 1:00PM" */
function formatCompactDateTime(isoString) {
	if (!isoString) return '';
	const d = new Date(isoString);
	const month = d.getMonth() + 1;
	const day = d.getDate();
	const hour = d.getHours();
	const min = d.getMinutes();
	const ampm = hour >= 12 ? 'PM' : 'AM';
	const hour12 = hour % 12 || 12;
	const minStr = min < 10 ? `0${min}` : String(min);
	return `${month}/${day} • ${hour12}:${minStr}${ampm}`;
}

const TOP_BOOKS = [
	{ label: 'BET99', value: 'BET99' },

	{ label: 'BetMGM', value: 'BetMGM' },

	{ label: 'Betano', value: 'Betano' },

	{ label: 'bet365', value: 'bet365' },

	{ label: 'BookMaker', value: 'BookMaker' },
	{ label: 'Caesars', value: 'Caesars' },
	{ label: 'Circa Vegas', value: 'Circa Vegas' },
	{ label: 'DraftKings', value: 'DraftKings' },
	{ label: 'ESPN BET', value: 'ESPN BET' },
	{ label: 'FanDuel', value: 'FanDuel' },
	{ label: 'Fanatics', value: 'Fanatics' },

	{ label: 'Pinnacle', value: 'Pinnacle' },

	{ label: 'PointsBet', value: 'PointsBet' },

	{ label: 'theScore', value: 'theScore' },
];

const topValues = TOP_BOOKS.map((book) => book.value);

function mergeGameGroupsByGameId(items) {
	if (!Array.isArray(items)) return items;

	const byId = new Map();

	for (const item of items) {
		if (!item) continue;
		const game = item.game || {};
		const id = item.game_id || game.game_id || game.id;

		// If we can't confidently identify the game, keep the item as-is under a unique key
		const key = id || Symbol('ungrouped');

		if (!byId.has(key)) {
			byId.set(key, {
				...item,
				books: Array.isArray(item.books) ? [...item.books] : [],
				selection: item.selection ? { ...item.selection } : {},
			});
			continue;
		}

		const existing = byId.get(key);

		// Merge books (unique)
		const existingBooks = Array.isArray(existing.books) ? existing.books : [];
		const incomingBooks = Array.isArray(item.books) ? item.books : [];
		const mergedBooksSet = new Set([...existingBooks, ...incomingBooks]);
		existing.books = Array.from(mergedBooksSet);

		// Merge selections deeply and dedupe by outcome_id_normalized + sportsbook
		const existingSelection = existing.selection || {};
		const incomingSelection = item.selection || {};

		for (const [selectionName, outcomes] of Object.entries(incomingSelection)) {
			if (!existingSelection[selectionName]) {
				existingSelection[selectionName] = { ...outcomes };
				continue;
			}
			const targetOutcomes = existingSelection[selectionName];
			for (const [outcomeId, arr] of Object.entries(outcomes || {})) {
				const prevArr = targetOutcomes[outcomeId] || [];
				const combined = [...prevArr, ...(Array.isArray(arr) ? arr : [])];
				const seen = new Set();
				const deduped = [];
				for (const o of combined) {
					if (!o) continue;
					const key = `${o.outcome_id_normalized || ''}|${o.sportsbook || ''}`;
					if (seen.has(key)) continue;
					seen.add(key);
					deduped.push(o);
				}
				targetOutcomes[outcomeId] = deduped;
			}
		}

		existing.selection = existingSelection;
		byId.set(key, existing);
	}

	return Array.from(byId.values());
}

function getRecommendedMarketForLeague(league) {
	const sport = LEAGUE_SPORT_MAP[league] || LEAGUE_SPORT_MAP.NBA;
	const popular = SPORT_TO_POPULAR_MARKETS[sport] || [];
	return popular[0] || 'Moneyline';
}

function sortBooks(books) {
	return books?.sort((a, b) => {
		const aIndex = topValues.indexOf(a);
		const bIndex = topValues.indexOf(b);

		if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
		if (aIndex === -1) return 1;
		if (bIndex === -1) return -1;

		return aIndex - bIndex;
	});
}

export const Screener2 = (props) => {
	const { accessToken } = props || {};
	const [filters, setFilters] = useState({ leagues: 'NBA', markets: getRecommendedMarketForLeague('NBA') });
	const [oddsData, setOddsData] = useState();
	const [uniqueBooks, setUniqueBooks] = useState();
	const [refreshCounter, setRefreshCounter] = useState(0);
	const [isLive, setIsLive] = useState(() => {
		try {
			const raw = window.localStorage.getItem('userdb');
			if (!raw) return false;
			const parsed = JSON.parse(raw);
			return Boolean(parsed?.live);
		} catch (e) {
			return false;
		}
	});
	const [selectedSelections, setSelectedSelections] = useState({});
	const [betActionAnchorEl, setBetActionAnchorEl] = useState(null);

	const [selectedBet, setSelectedBet] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDetailView, setIsDetailView] = useState(false);
	const [selectedDetailOutcomeId, setSelectedDetailOutcomeId] = useState();
	const [selectedDetailData, setSelectedDetailData] = useState();

	const [whichFiltersOpen, setWhichFiltersOpen] = useState({ leagues: false, sportsbooks: false, sharpBooks: false, markets: false, marketTypes: false });
	const email = JSON.parse(window.localStorage.getItem('userdb'))?.email;
	const [viewAllBooks, setViewAllBooks] = useState(true);
	const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
	const [hasUserModifiedSportsbooksSelection, setHasUserModifiedSportsbooksSelection] = useState(false);
	const [sportsbooksAnchorEl, setSportsbooksAnchorEl] = useState(null);
	const [sportsbookSearch, setSportsbookSearch] = useState('');
	const [searchQuery, setSearchQuery] = useState('');

	const effectiveLeague = filters?.leagues || 'NBA';
	const sportForLeague = LEAGUE_SPORT_MAP[effectiveLeague];
	const popularMarkets = SPORT_TO_POPULAR_MARKETS[sportForLeague] || [];
	const marketTabsSource = popularMarkets.length ? popularMarkets : (SPORT_TO_MARKET_MAP[sportForLeague] || []);
	const marketTabs = marketTabsSource.slice(0, 8);
	const displayedBooks = sortBooks((uniqueBooks || []).filter((b) => selectedSportsbooks.includes(b)));
	const booksToShow = displayedBooks.length ? displayedBooks : sortBooks(uniqueBooks || []);
	const searchParsed = parseSearchQuery(searchQuery);
	const { text: searchText, line: searchLine } = searchParsed;

	const filteredOddsData = (() => {
		if (!oddsData) return oddsData;
		if (!searchQuery.trim()) return oddsData;
		return oddsData.filter((od) => {
			const g = getGameDisplay(od?.game);
			const home = (g?.home_team_display ?? '').toLowerCase();
			const away = (g?.away_team_display ?? '').toLowerCase();
			const teamMatch = !searchText || home.includes(searchText) || away.includes(searchText);
			const selectionEntries = Object.entries(od?.selection ?? {}).filter(([name, outcomes]) => {
				const nameMatch = !searchText || name.toLowerCase().includes(searchText);
				if (!nameMatch && !teamMatch) return false;
				if (searchLine == null) return true;
				return Object.keys(outcomes).some((outcomeId) => {
					const lineNum = parseFloat(outcomeId.split(':').pop());
					return !isNaN(lineNum) && lineNum === searchLine;
				});
			});
			return selectionEntries.length > 0;
		});
	})();

	const tableRef = useRef(null);
	const tableContainerRef = useRef(null);
	const [headerHeight, setHeaderHeight] = useState(48);

	const tableRefCallback = useCallback((node) => {
		tableRef.current = node;
		if (node) {
			const thead = node.querySelector('thead');
			if (thead) {
				setHeaderHeight(thead.getBoundingClientRect().height);
			}
		}
	}, []);

	const {
		items: oddsItems,
		isInitialLoading,
		isLoadingMore,
		hasMore,
		error: oddsError,
		loadMore,
		reset: resetOdds,
	} = useInfiniteOddsScreen({
		league: filters?.leagues || 'NBA',
		market: filters?.markets,
		isLive,
		pageSize: ODDS_SCREEN_PAGE_SIZE,
		accessToken,
		refreshKey: refreshCounter,
	});

	const { highlightedPrices } = useOddsStream({
		league: filters?.leagues || 'NBA',
		market: filters?.markets,
		isLive,
		accessToken,
		oddsData,
		setOddsData,
	});

	useEffect(() => {
		if (Array.isArray(oddsItems)) {
			const merged = mergeGameGroupsByGameId(oddsItems);
			setOddsData(merged);
			try {
				const rawBooks = merged.flatMap((item) => item.books || []).filter((b) => !isExcludedScreenerBook(b));
				const uniquebks = normalizeSportsbookList(rawBooks).sort();
				setUniqueBooks(uniquebks);
				if (!hasUserModifiedSportsbooksSelection) {
					setSelectedSportsbooks(uniquebks);
				}
			} catch (e) {}
		}
	}, [oddsItems, hasUserModifiedSportsbooksSelection]);

	// Hide full-screen loader after page 1 completes (with or without rows).
	useEffect(() => {
		if (!isInitialLoading) {
			setIsLoading(false);
		}
	}, [isInitialLoading]);

	// Prefetch page 2+ only after page 1 has landed — avoids racing page 2
	// against page 1 (which would discard page 1's response via requestId).
	useEffect(() => {
		if (!isInitialLoading && !isLoadingMore && hasMore && oddsItems.length > 0) {
			loadMore();
		}
	}, [isInitialLoading, isLoadingMore, hasMore, loadMore, oddsItems.length]);

	// When league changes, ensure selected market exists for that league; otherwise reset to recommended
	useEffect(() => {
		const league = filters?.leagues || 'NBA';
		const sport = LEAGUE_SPORT_MAP[league];
		const allowedMarkets = SPORT_TO_MARKET_MAP[sport] || [];
		const currentMarket = filters?.markets;
		if (currentMarket && allowedMarkets.length > 0 && !allowedMarkets.includes(currentMarket)) {
			setFilters((prev) => ({ ...prev, markets: getRecommendedMarketForLeague(league) }));
		}
	}, [filters?.leagues]);

	// When core filters change, show the loading indicator again until fresh
	// data for the new context arrives.
	useEffect(() => {
		setIsLoading(true);
	}, [filters?.leagues, filters?.markets, isLive, refreshCounter]);

	useEffect(() => {
		try {
			const raw = window.localStorage.getItem('userdb');
			if (!raw) return;
			const parsed = JSON.parse(raw) || {};
			parsed.live = isLive;
			window.localStorage.setItem('userdb', JSON.stringify(parsed));
		} catch (e) {}
	}, [isLive]);

	const handleBetClick = (betData) => {
		setSelectedBet(betData);
	};

	useEffect(() => {
		if (selectedDetailOutcomeId && oddsData) {
			const games = [];
			for (const item of oddsData) {
				games.push({
					game: getGameDisplay(item.game),
					books: item.books,
					data: item.selection[selectedDetailOutcomeId.split(':')?.[2]],
				});
			}
			setSelectedDetailData(games);
		}
	}, [selectedDetailOutcomeId, oddsData]);

	const allBooksList = sortBooks([...new Set([...topValues, ...(uniqueBooks || [])])]) || [];
	const filteredBooksList = sportsbookSearch.trim()
		? allBooksList.filter((b) => b.toLowerCase().includes(sportsbookSearch.trim().toLowerCase()))
		: allBooksList;

	return (
		<div className="screener-root">
			<Helmet>
				<title>Sportsbook Odds Screener | Compare Lines Across 20+ Books | WagerWiz</title>
				<meta name="description" content="Compare live sportsbook odds side by side across DraftKings, FanDuel, BetMGM, Caesars, ESPN BET, Pinnacle and more. Free odds screener for NFL, NBA, MLB, NHL, soccer and college sports." />
				<link rel="canonical" href="https://www.thewagerwiz.com/screener" />
				<meta property="og:title" content="Sportsbook Odds Screener | WagerWiz" />
				<meta property="og:description" content="Compare live sportsbook odds side by side across 20+ books. Free odds screener for NFL, NBA, MLB, NHL, soccer and college sports." />
				<meta property="og:type" content="website" />
				<meta property="og:url" content="https://www.thewagerwiz.com/screener" />
				<meta property="og:site_name" content="WagerWiz" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content="Sportsbook Odds Screener | WagerWiz" />
				<meta name="twitter:description" content="Compare live sportsbook odds side by side across 20+ books." />
			</Helmet>
			<Typography variant="h5" className="screener-page-title">Sportsbook Screener</Typography>
			<div className="screener-filters">
				<div className="screener-filters-left">
				<ScreenerAutocomplete
					options={LEAGUES}
					value={filters?.leagues || []}
					whichFiltersOpen={whichFiltersOpen}
					setWhichFiltersOpen={setWhichFiltersOpen}
					filters={filters}
					setFilters={setFilters}
					email={email}
					filterName={'leagues'}
					filterTitle="League"
					filterPlaceholder="league"
					setRefreshCounter={setRefreshCounter}
					refreshCounter={refreshCounter}
				/>
				<ScreenerMarketsDropdown
					options={(SPORT_TO_MARKET_MAP[LEAGUE_SPORT_MAP[effectiveLeague]] || []).map((m) => ({ value: m, label: m }))}
					value={filters?.markets}
					whichFiltersOpen={whichFiltersOpen}
					setWhichFiltersOpen={setWhichFiltersOpen}
					filters={filters}
					setFilters={setFilters}
					filterName={'markets'}
					filterTitle="Market"
					filterPlaceholder="market"
					setRefreshCounter={setRefreshCounter}
					refreshCounter={refreshCounter}
					recommendedMarkets={marketTabs.map((m) => ({ value: m, label: m }))}
				/>
				<Button
					variant="outlined"
					size="small"
					className="screener-sportsbooks-btn"
					onClick={(e) => setSportsbooksAnchorEl(e.currentTarget)}>
					Sportsbooks
				</Button>
				<Popover
					open={Boolean(sportsbooksAnchorEl)}
					anchorEl={sportsbooksAnchorEl}
					onClose={() => { setSportsbooksAnchorEl(null); setSportsbookSearch(''); }}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					PaperProps={{ className: 'screener-popover-paper screener-sportsbooks-popover', sx: { maxHeight: 420, minWidth: 300, p: 0, overflow: 'hidden', borderRadius: 2 } }}>
					<div className="screener-sportsbooks-popover-inner">
						<div className="screener-sportsbooks-popover-header">
							<Typography variant="subtitle2" className="screener-sportsbooks-popover-title">Sportsbooks</Typography>
							<InputBase
								className="screener-sportsbooks-search"
								placeholder="Search…"
								value={sportsbookSearch}
								onChange={(e) => setSportsbookSearch(e.target.value)}
								onClick={(e) => e.stopPropagation()}
								startAdornment={
									<InputAdornment position="start" className="screener-sportsbooks-search-icon">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								}
								sx={{ '& input': { py: 1.25, fontSize: '0.875rem' } }}
							/>
						</div>
						{filteredBooksList.length === 0 ? (
							<div className="screener-sportsbooks-empty">
								<Typography variant="body2">No sportsbooks match your search.</Typography>
							</div>
						) : (
							<List className="screener-sportsbooks-list" sx={{ bgcolor: 'transparent', color: 'var(--screener-text)', py: 0.5, maxHeight: 340, overflowY: 'auto' }}>
								{filteredBooksList.map((book) => {
									const isChecked = selectedSportsbooks.includes(book);
									return (
										<ListItemButton
											key={book}
											className="screener-sportsbooks-list-item"
											onClick={() => {
												setHasUserModifiedSportsbooksSelection(true);
												setSelectedSportsbooks((prev) =>
													prev.includes(book) ? prev.filter((b) => b !== book) : [...prev, book]
												);
											}}
											selected={isChecked}
											disableRipple
											sx={{
												py: 1,
												px: 1.5,
												borderRadius: 1,
												mx: 1,
												my: 0.25,
												minHeight: 44,
												'&.Mui-selected': {
													backgroundColor: 'rgba(59, 130, 246, 0.08)',
													'&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.12)' },
												},
												'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
											}}
										>
											<Checkbox
												checked={isChecked}
												disableRipple
												size="small"
												readOnly
												className="screener-sportsbooks-checkbox"
												sx={{ color: 'var(--screener-text-muted)', mr: 1.5, p: 0.5, pointerEvents: 'none', '&.Mui-checked': { color: 'var(--screener-accent)' } }}
											/>
											<div className="screener-sportsbooks-list-item-logo">
												<LogoGenerator sportsbook={book} smallLogos customSize={32} />
											</div>
											<span className="screener-sportsbooks-list-item-name">{getSportsbookDisplayName(book)}</span>
										</ListItemButton>
									);
								})}
							</List>
						)}
					</div>
				</Popover>
				<InputBase
					className="screener-search"
					placeholder="Search player, team, or line (e.g. cason wallace 2.5)…"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					startAdornment={
						<InputAdornment position="start" sx={{ color: 'var(--screener-text-muted)' }}>
							<SearchIcon fontSize="small" />
						</InputAdornment>
					}
					sx={{
						padding: '8px 14px',
						color: 'var(--screener-text)',
						minWidth: 220,
						'& input::placeholder': { opacity: 0.7 },
					}}
				/>
				</div>
				<div className="screener-filters-right-group">
					<div className="screener-pregame-live-toggle" aria-label="Toggle pregame or live odds">
						<div
							className={`screener-pregame-live-option ${!isLive ? 'active' : ''}`}
							role="button"
							tabIndex={0}
							onClick={() => setIsLive(false)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									setIsLive(false);
								}
							}}>
							Pregame
						</div>
						<div
							className={`screener-pregame-live-option ${isLive ? 'active' : ''}`}
							role="button"
							tabIndex={0}
							onClick={() => setIsLive(true)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									setIsLive(true);
								}
							}}>
							Live
						</div>
					</div>
					<div className="screener-view-all">
						<span>View All Books</span>
						<IOSSwitch
							onChange={(e) => {
								const checked = e.target.checked;
								setViewAllBooks(checked);
								if (checked && uniqueBooks && uniqueBooks.length) {
									setHasUserModifiedSportsbooksSelection(false);
									setSelectedSportsbooks(uniqueBooks);
								}
							}}
							checked={viewAllBooks}
						/>
					</div>
				</div>
			</div>
			{marketTabs.length > 0 && (
				<div className="screener-market-tabs-section">
					<div className="screener-market-tabs-label">
						Popular Markets
					</div>
					<div className="screener-market-tabs">
					{marketTabs.map((market) => (
						<Button
							key={market}
							variant={filters?.markets === market ? 'contained' : 'outlined'}
							size="small"
							className={`screener-market-tab ${filters?.markets === market ? 'screener-market-tab-contained' : 'screener-market-tab-outlined'}`}
							onClick={() => setFilters((prev) => ({ ...prev, markets: market }))}>
							{market}
						</Button>
					))}
					</div>
				</div>
			)}
			<div>
				{/* table section */}
				{!isLoading && !isDetailView && (
					<>
						{oddsError && (
							<div className="screener-empty">
								Failed to load odds.{' '}
								<button
									type="button"
									onClick={() => {
										resetOdds();
									}}>
									Retry
								</button>
							</div>
						)}
						{(!filteredOddsData || filteredOddsData.length === 0) && !oddsError && (
							<div className="screener-empty">
								No odds found for this market. Try a different player prop, league, sportsbook set, or switch between pregame and live.
							</div>
						)}
						{filteredOddsData && filteredOddsData.length > 0 && (
							<>
								<TableContainer
									component={Paper}
									className="screener-table-wrap"
									sx={{ maxHeight: '95vh', overflowX: 'auto', overflowY: 'auto' }}
									ref={tableContainerRef}>
									<Table ref={tableRefCallback} stickyHeader className="screener-table">
								<TableHead>
									<TableRow>
										<TableCell className="screener-header-sticky" sx={{ position: 'sticky', left: 0, top: 0, zIndex: 10, minWidth: 220, maxWidth: 220 }}>Bet</TableCell>
										<TableCell align="right" className="screener-col-line">{/* line */}</TableCell>
										<TableCell align="right" className="screener-col-best">BEST</TableCell>
										<TableCell align="right" className="screener-col-average">AVERAGE</TableCell>
										{booksToShow?.map((uniqueBook) => (
											<TableCell key={uniqueBook} align="right" className="screener-col-book" sx={{ overflow: 'hidden', zIndex: 4 }}>
												<LogoGenerator sportsbook={uniqueBook} />
											</TableCell>
										))}
									</TableRow>
								</TableHead>
									<TableBody>
										{filteredOddsData?.map((od, index) => {
									const g = getGameDisplay(od?.game);
									const homeForRow = (g?.home_team_display ?? '').toLowerCase();
									const awayForRow = (g?.away_team_display ?? '').toLowerCase();
									const teamMatch = !searchText || homeForRow.includes(searchText) || awayForRow.includes(searchText);
									const selectionEntries = Object.entries(od?.selection ?? {}).filter(([name, outcomes]) => {
										const nameMatch = !searchText || name.toLowerCase().includes(searchText);
										if (!nameMatch && !teamMatch) return false;
										if (searchLine == null) return true;
										const hasMatchingLine = Object.keys(outcomes).some((outcomeId) => {
											const linePart = outcomeId.split(':').pop();
											const lineNum = parseFloat(linePart);
											return !isNaN(lineNum) && lineNum === searchLine;
										});
										return hasMatchingLine;
									});
									return selectionEntries.map(([name, outcomes], index) => {
										const [bestOutcomeId] = Object.entries(outcomes).reduce((max, curr) => (curr[1].length > max[1].length ? curr : max));

										const selectionOptions = Object.keys(outcomes)
											.sort((a, b) => {
												const numA = parseFloat(a.split(':').pop());
												const numB = parseFloat(b.split(':').pop());
												return numA - numB;
											})
											.map((slctn) => {
												return {
													selection: slctn.split(':').at(-1),
													id: slctn,
												};
											});
										const rowKey = `${g.id}_${name}`;
										const selected = selectedSelections[rowKey];
										const selectedOutcomeId = selected?.id;

										const outcomeIdMatchingSearchLine =
											searchLine != null
												? Object.keys(outcomes).find((outcomeId) => {
														const linePart = outcomeId.split(':').pop();
														const lineNum = parseFloat(linePart);
														return !isNaN(lineNum) && lineNum === searchLine;
													})
												: null;

										const outcomeIdToUse = outcomeIdMatchingSearchLine || selectedOutcomeId || bestOutcomeId;
										let pricesToUse = outcomes[outcomeIdToUse];
										let isHomeTeamNegativeSpread = false;
										let overSpreadNum = NaN;
										let underSpreadNum = NaN;

										if (outcomeIdToUse.includes('Moneyline')) {
											const split = pricesToUse?.[0]?.outcome_id_normalized_no_book?.split(':');
											const otherSelection = split && g.home_team_display === split[2] ? g.away_team_display : (split ? g.home_team_display : null);
											const other_outcome_id_normalized_no_book = split && otherSelection ? `${split[0]}:${split[1]}:${otherSelection}:` : null;
											const otherPrices = (other_outcome_id_normalized_no_book && od.selection?.[otherSelection]?.[other_outcome_id_normalized_no_book]) || [];

											const currentByBook = mergePricesByDisplaySportsbook(pricesToUse || []);
											const otherByBook = mergePricesByDisplaySportsbook(otherPrices);
											const allBooks = new Set([...Object.keys(currentByBook), ...Object.keys(otherByBook)]);

											if (allBooks.size > 0) {
												pricesToUse = Array.from(allBooks).map((book) => {
													const curr = currentByBook[book];
													const other = otherByBook[book];
													const base = curr || other;
													return {
														...base,
														sportsbook: book,
														bet_price_over: curr?.bet_price_over != null ? curr.bet_price_over : base?.bet_price_over,
														bet_price_under: other?.bet_price_over != null ? other.bet_price_over : (other?.bet_price_under ?? base?.bet_price_under),
														deep_link_over: (curr || base)?.deep_link_over ?? base?.deep_link_over,
														deep_link_under: (other || base)?.deep_link_over ?? (other || base)?.deep_link_under ?? base?.deep_link_under,
														outcome_id_normalized: curr?.outcome_id_normalized ?? base?.outcome_id_normalized,
														outcome_id_normalized_over: curr?.outcome_id_normalized,
														outcome_id_normalized_under: other?.outcome_id_normalized,
													};
												});
											}
										} else if (outcomeIdToUse.includes('Point Spread') || outcomeIdToUse.includes('Asian Handicap')) {
											const split = pricesToUse?.[0]?.outcome_id_normalized_no_book?.split(':');
											const otherSelection = split && g.home_team_display === split[2] ? g.away_team_display : (split ? g.home_team_display : null);
											const otherSpreadStr = split ? toggleNegative(split[3]) : null;
											const other_outcome_id_normalized_no_book = split && otherSelection && otherSpreadStr != null ? `${split[0]}:${split[1]}:${otherSelection}:${otherSpreadStr}` : null;
											const otherPrices = (other_outcome_id_normalized_no_book && od.selection?.[otherSelection]?.[other_outcome_id_normalized_no_book]) || [];

											const currSpreadNum = split ? parseFloat(split[3]) : NaN;
											const otherSpreadNum = otherSpreadStr != null ? parseFloat(otherSpreadStr) : NaN;

											if (split) {
												if (split[2] === g.home_team_display && currSpreadNum < 0) isHomeTeamNegativeSpread = true;
												if (split[2] === g.away_team_display && currSpreadNum > 0) isHomeTeamNegativeSpread = true;
											}

											const currentByBook = mergePricesByDisplaySportsbook(pricesToUse || []);
											const otherByBook = mergePricesByDisplaySportsbook(otherPrices);
											const allBooks = new Set([...Object.keys(currentByBook), ...Object.keys(otherByBook)]);

											overSpreadNum = index === 0 ? currSpreadNum : otherSpreadNum;
											underSpreadNum = index === 0 ? otherSpreadNum : currSpreadNum;

											if (allBooks.size > 0) {
												const forOver = index === 0 ? currentByBook : otherByBook;
												const forUnder = index === 0 ? otherByBook : currentByBook;
												pricesToUse = Array.from(allBooks).map((book) => {
													const curr = currentByBook[book];
													const other = otherByBook[book];
													const overEntry = forOver[book];
													const underEntry = forUnder[book];
													const base = curr || other;
													const overPrice = overEntry?.bet_price_over_positive ?? overEntry?.bet_price_over_negative ?? overEntry?.bet_price_over;
													const underPrice = underEntry?.bet_price_under_positive ?? underEntry?.bet_price_under_negative ?? underEntry?.bet_price_over_positive ?? underEntry?.bet_price_over_negative ?? underEntry?.bet_price_over;
													return {
														...base,
														sportsbook: book,
														bet_price_over_positive: overEntry?.bet_price_over_positive ?? (overSpreadNum > 0 ? overPrice : undefined) ?? base?.bet_price_over_positive,
														bet_price_over_negative: overEntry?.bet_price_over_negative ?? (overSpreadNum < 0 ? overPrice : undefined) ?? base?.bet_price_over_negative,
														bet_price_under_positive: underEntry?.bet_price_under_positive ?? (underSpreadNum > 0 ? underPrice : undefined) ?? underEntry?.bet_price_over_positive ?? base?.bet_price_under_positive,
														bet_price_under_negative: underEntry?.bet_price_under_negative ?? (underSpreadNum < 0 ? underPrice : undefined) ?? underEntry?.bet_price_over_negative ?? base?.bet_price_under_negative,
														deep_link_over: (curr || base)?.deep_link_over ?? base?.deep_link_over,
														deep_link_under: (other || base)?.deep_link_over ?? (other || base)?.deep_link_under ?? base?.deep_link_under,
														outcome_id_normalized: curr?.outcome_id_normalized ?? base?.outcome_id_normalized,
														outcome_id_normalized_over: curr?.outcome_id_normalized,
														outcome_id_normalized_under: other?.outcome_id_normalized,
													};
												});
											}
										} else {
											const raw = pricesToUse || [];
											const overEntries = raw.filter((p) => p?.outcome_id_normalized?.includes(':over:'));
											const underEntries = raw.filter((p) => p?.outcome_id_normalized?.includes(':under:'));
											const overByBook = mergePricesByDisplaySportsbook(overEntries);
											const underByBook = mergePricesByDisplaySportsbook(underEntries);
											const allBooks = new Set([...Object.keys(overByBook), ...Object.keys(underByBook)]);
											if (overEntries.length > 0 || underEntries.length > 0) {
												pricesToUse = Array.from(allBooks).map((book) => {
													const o = overByBook[book];
													const u = underByBook[book];
													const base = o || u;
													return {
														...base,
														sportsbook: book,
														bet_price_over: o?.bet_price_over != null ? o.bet_price_over : base?.bet_price_over,
														bet_price_under: u?.bet_price_over != null ? u.bet_price_over : (u?.bet_price_under ?? base?.bet_price_under),
														deep_link_over: (o || base)?.deep_link_over ?? base?.deep_link_over,
														deep_link_under: (u || base)?.deep_link_over ?? (u || base)?.deep_link_under ?? base?.deep_link_under,
														outcome_id_normalized: o?.outcome_id_normalized ?? base?.outcome_id_normalized,
														outcome_id_normalized_over: o?.outcome_id_normalized,
														outcome_id_normalized_under: u?.outcome_id_normalized,
													};
												});
											}
										}

										pricesToUse = (pricesToUse || []).filter((p) => !isExcludedScreenerBook(p?.sportsbook));
										let priceMap;

										try {
											priceMap = mergePricesByDisplaySportsbook(pricesToUse);
										} catch (e) {}

										const betStats = getBetPriceStats(pricesToUse);
										// Moneyline merge puts this row's team in bet_price_over and the opponent in bet_price_under; both team rows must read "over".
										const spreadSideForRow = index === 0 ? 'over' : 'under';
										const spreadNumForRow = index === 0 ? overSpreadNum : underSpreadNum;
										const bestBook = filters?.markets === 'Moneyline'
											? betStats.best_sportsbook_over
											: (filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap')
												? spreadBestBookFromStats(betStats, spreadSideForRow, spreadNumForRow)
												: (index === 0 ? betStats.best_sportsbook_over : betStats.best_sportsbook_under);

										return (
											<>
												{index === 0 && (
													<TableRow key={g?.id} className="screener-game-row">
														<TableCell
															className="screener-game-row-sticky"
															sx={{
																position: 'sticky',
																left: 0,
																top: headerHeight,
																zIndex: 3,
																minWidth: 220,
																maxWidth: 220,
																backgroundColor: '#1c1f26',
																boxShadow: '2px 0 6px rgba(0,0,0,0.4)',
															}}>
															<div>
																{g?.away_team_display} @ {g?.home_team_display}
															</div>
															<div className="screener-game-meta">{formatCompactDateTime(g?.start_date)}</div>
														</TableCell>
														<TableCell
															className="screener-game-row-spacer"
															sx={{
																position: 'sticky',
																top: headerHeight,
																zIndex: 2,
																padding: 0,
																border: 0,
																backgroundColor: '#1c1f26',
															}}
															colSpan={(uniqueBooks?.length ?? 0) + 4}
														/>
													</TableRow>
												)}

												<TableRow key={bestOutcomeId}>
													<TableCell
														className="screener-cell-sticky"
														sx={{
															position: 'sticky',
															left: 0,
															zIndex: 2,
															minWidth: 220,
															maxWidth: 220,
														}}>
														{outcomes[bestOutcomeId][0].points && (
															<div className="flex flex-row">
																<div
																	style={{ width: 150, marginRight: 10, alignContent: 'center', cursor: 'pointer' }}
																	onClick={() => {
																		setIsDetailView(true);
																		setSelectedDetailOutcomeId(outcomeIdToUse);
																	}}>
																	{name}
																</div>
																<SelectionPopover
																	selectionOptions={selectionOptions}
																	points={outcomes[bestOutcomeId][0].points}
																										isSpread={outcomes[bestOutcomeId][0].market === 'Point Spread' || outcomes[bestOutcomeId][0].market === 'Asian Handicap'}
									isEditable={(outcomes[bestOutcomeId][0].market === 'Point Spread' || outcomes[bestOutcomeId][0].market === 'Asian Handicap') ? index === 0 : true}
									selectedValue={selectedSelections[rowKey]}
									bestOutcomeId={bestOutcomeId}
									onSelect={(value) => {
																		if (!(outcomes[bestOutcomeId][0].market === 'Point Spread' || outcomes[bestOutcomeId][0].market === 'Asian Handicap')) {
																			setSelectedSelections((prev) => ({
																				...prev,
																				[rowKey]: value,
																			}));
																		} else {
																			const otherRowKey =
																				`${g.id}_${g.home_team_display}` !== rowKey
																					? `${g.id}_${g.home_team_display}`
																					: `${g.id}_${g.away_team_display}`;
																			const otherTeam = `${g.id}_${g.home_team_display}` !== rowKey ? g.home_team_display : g.away_team_display;
																			const otherValue = {
																				selection: toggleNegative(value.selection),
																				id: replaceTeam(toggleFinalNumberSign(value.id), otherTeam),
																			};

																			setSelectedSelections((prev) => ({
																				...prev,
																				[rowKey]: value,
																				[otherRowKey]: otherValue,
																			}));
																		}
																	}}
																/>
															</div>
														)}
														{!outcomes[bestOutcomeId][0].points && outcomes[bestOutcomeId][0].selection}
													</TableCell>
													<TableCell align="right" className="screener-col-line">{/* line */}</TableCell>
													<TableCell align="right" className="screener-col-best">
														{(() => {
															const moneylineBestOver = addPlusSign(formatAmerican(betStats.highest_bet_price_over));
															const moneylineBestUnder = addPlusSign(formatAmerican(betStats.highest_bet_price_under));
															const nonMlBestOver = moneylineBestOver;
															const nonMlBestUnder = moneylineBestUnder;

															const renderBestCell = (value, hasLogo) => {
																const isMissing = value == null;
																return (
																	<span className={`screener-best-cell ${isMissing ? 'screener-best-cell-empty' : ''}`}>
																		{isMissing ? NO_ODDS_PLACEHOLDER : value}
																		{!isMissing && hasLogo}
																	</span>
																);
															};

															return (
																<Table size="small">
																	{filters?.markets === 'Moneyline' && (
																		<TableBody>
																			<TableRow>
																				<TableCell component="td">{renderBestCell(moneylineBestOver, bestBook && <LogoGenerator sportsbook={bestBook} smallLogos customSize={22} />)}</TableCell>
																			</TableRow>
																		</TableBody>
																	)}
																	{filters?.markets !== 'Moneyline' &&
																		filters?.markets !== 'Point Spread' &&
																		filters?.markets !== 'Asian Handicap' && (
																			<TableBody>
																				<TableRow>
																					<TableCell component="td">{renderBestCell(
																						nonMlBestOver,
																						betStats.best_sportsbook_over && (
																							<LogoGenerator sportsbook={betStats.best_sportsbook_over} smallLogos customSize={22} />
																						)
																					)}</TableCell>
																				</TableRow>
																				<TableRow>
																					<TableCell component="td">{renderBestCell(
																						nonMlBestUnder,
																						betStats.best_sportsbook_under && (
																							<LogoGenerator sportsbook={betStats.best_sportsbook_under} smallLogos customSize={22} />
																						)
																					)}</TableCell>
																				</TableRow>
																			</TableBody>
																		)}
																	{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																		<TableBody>
																			{index === 0 ? (
																				<TableRow>
																					<TableCell component="td">{renderBestCell(
																						addPlusSign(spreadHighestFromStats(betStats, 'over', overSpreadNum)),
																						bestBook && <LogoGenerator sportsbook={bestBook} smallLogos customSize={22} />
																					)}</TableCell>
																				</TableRow>
																			) : (
																				<TableRow>
																					<TableCell component="td">{renderBestCell(
																						addPlusSign(spreadHighestFromStats(betStats, 'under', underSpreadNum)),
																						bestBook && <LogoGenerator sportsbook={bestBook} smallLogos customSize={22} />
																					)}</TableCell>
																				</TableRow>
																			)}
																		</TableBody>
																	)}
																</Table>
															);
														})()}
													</TableCell>
													<TableCell align="right" className="screener-col-average">
														<Table>
															{filters?.markets === 'Moneyline' && (
																<TableBody>
																	<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
																</TableBody>
															)}
															{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
																<TableBody>
																	<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
																	<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_under))}</span></TableCell></TableRow>
																</TableBody>
															)}
															{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																<TableBody>
																	{index === 0 ? (
																		<TableRow>
																			<TableCell component="td"><span className="screener-average-cell">
																				{addPlusSign(spreadAverageFromStats(betStats, 'over', overSpreadNum))}
																			</span></TableCell>
																		</TableRow>
																	) : (
																		<TableRow>
																			<TableCell component="td"><span className="screener-average-cell">
																				{addPlusSign(spreadAverageFromStats(betStats, 'under', underSpreadNum))}
																			</span></TableCell>
																		</TableRow>
																	)}
																</TableBody>
															)}
														</Table>
													</TableCell>
													{booksToShow?.map((book) => {
														const rawOver = resolveDeepLink(priceMap?.[book]?.deep_link_over);
														const rawUnder = resolveDeepLink(priceMap?.[book]?.deep_link_under);
														let link_over = rawOver ? (rawOver.includes('betmgm') ? rawOver.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawOver.replace(/<STATE>./g, '')) : '';
														let link_under = rawUnder ? (rawUnder.includes('betmgm') ? rawUnder.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawUnder.replace(/<STATE>./g, '')) : '';
														let spreadPriceHome = addPlusSign(
															spreadAmericanFromPriceRow(priceMap?.[book], 'over', overSpreadNum)
														);
													let spreadPriceAway = addPlusSign(
														spreadAmericanFromPriceRow(priceMap?.[book], 'under', underSpreadNum)
													);
													const hlOver = getHighlightClass(highlightedPrices, priceMap?.[book]?.outcome_id_normalized_over, priceMap?.[book]?.outcome_id_normalized);
													const hlUnder = getHighlightClass(highlightedPrices, priceMap?.[book]?.outcome_id_normalized_under, priceMap?.[book]?.outcome_id_normalized);
													return (
																<TableCell key={book} align="right" className="screener-col-book">
																	<Popover
																		onClose={(e) => {
																			setSelectedBet();
																		}}
																		PaperProps={{
																			className: 'screener-popover-paper',
																			sx: { p: 1 },
																		}}
																		anchorOrigin={{ vertical: 'bottom' }}
																		anchorEl={betActionAnchorEl}
																		open={selectedBet?.outcomeId && (selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_over || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_under)}>
																		<BetActionsOverlay
																			linkOver={link_over}
																			linkUnder={link_under}
																			betPriceOver={
																				(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') ? spreadPriceHome : addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																			}
																			betPriceUnder={
																				(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') ? spreadPriceAway : addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																			}
																			sportsbook={book}
																			isSpread={outcomes[bestOutcomeId][0].market === 'Point Spread' || outcomes[bestOutcomeId][0].market === 'Asian Handicap'}
																			isHomeTeamNegativeSpread={isHomeTeamNegativeSpread}
																			outcome_id_normalized={priceMap?.[book]?.outcome_id_normalized}
																			sport={priceMap?.[book]?.sport}
																			sportsbooks={uniqueBooks}
																		/>
																	</Popover>
																	<Table>
																		{filters?.markets === 'Moneyline' && (
																			<TableBody>
																				<TableRow
																					onClick={(e) => {
																						setBetActionAnchorEl(e.currentTarget);
																						handleBetClick({
																							type: 'over',
																							book,
																							outcomeId: priceMap?.[book]?.outcome_id_normalized_over ?? priceMap?.[book]?.outcome_id_normalized,
																						});
																					}}
																					sx={{ cursor: 'pointer' }}>
																					<TableCell component="td"><div className={`screener-odds-pill ${priceMap?.[book]?.bet_price_over != null ? 'has-value' : 'no-value'} ${hlOver}`}>
																						{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																							? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																							: NO_ODDS_PLACEHOLDER}
																					</div></TableCell>
																				</TableRow>
																											</TableBody>
																		)}
																		{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
																			<TableBody>
																				<TableRow
																					onClick={(e) => {
																						setBetActionAnchorEl(e.currentTarget);
																						handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized_over ?? priceMap?.[book]?.outcome_id_normalized });
																					}}
																					sx={{ cursor: 'pointer' }}>
																					<TableCell component="td"><div className={`screener-odds-pill ${priceMap?.[book]?.bet_price_over != null ? 'has-value' : 'no-value'} ${hlOver}`}>
																						{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																							? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																							: NO_ODDS_PLACEHOLDER}
																					</div></TableCell>
																				</TableRow>
																				<TableRow
																					onClick={(e) => {
																						setBetActionAnchorEl(e.currentTarget);
																						handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized_under ?? priceMap?.[book]?.outcome_id_normalized });
																					}}
																					sx={{ cursor: 'pointer' }}>
																					<TableCell component="td"><div className={`screener-odds-pill ${priceMap?.[book]?.bet_price_under != null ? 'has-value' : 'no-value'} ${hlUnder}`}>
																						{formatAmerican(priceMap?.[book]?.bet_price_under) != null
																							? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																							: NO_ODDS_PLACEHOLDER}
																					</div></TableCell>
																				</TableRow>
																			</TableBody>
																		)}
																		{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																			<TableBody>
																				{index === 0 ? (
																					<TableRow
																						onClick={(e) => {
																							setBetActionAnchorEl(e.currentTarget);
																							handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																						}}
																						sx={{ cursor: 'pointer' }}>
																						<TableCell component="td"><div className={`screener-odds-pill ${!isNaN(parseFloat(String(spreadPriceHome).replace(/[+-]/g, ''))) ? 'has-value' : 'no-value'} ${hlOver}`}>
																							{!isNaN(spreadPriceHome) ? spreadPriceHome : NO_ODDS_PLACEHOLDER}
																						</div></TableCell>
																					</TableRow>
																				) : (
																					<TableRow
																						onClick={(e) => {
																							setBetActionAnchorEl(e.currentTarget);
																							handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																						}}
																						sx={{ cursor: 'pointer' }}>
																						<TableCell component="td"><div className={`screener-odds-pill ${!isNaN(parseFloat(String(spreadPriceAway).replace(/[+-]/g, ''))) ? 'has-value' : 'no-value'} ${hlUnder}`}>
																							{!isNaN(spreadPriceAway) ? spreadPriceAway : NO_ODDS_PLACEHOLDER}
																						</div></TableCell>
																					</TableRow>
																				)}
																			</TableBody>
																		)}
																	</Table>
																</TableCell>
															);
													})}
												</TableRow>
											</>
										);
									});
								})}
								<TableRow>
									<TableCell colSpan={(booksToShow?.length ?? 0) + 4} align="center">
										<div style={{ height: 1 }} />
										{isLoadingMore && <div className="screener-loading-more">Loading more…</div>}
										{!hasMore && oddsData && oddsData.length > 0 && (
											<div className="screener-end-of-results">End of results</div>
										)}
									</TableCell>
								</TableRow>
							</TableBody>
									</Table>
								</TableContainer>
							</>
						)}
					</>
				)}
				{!isLoading && isDetailView && (
					<div>
						<h3 style={{ fontSize: 20 }}>
							{selectedDetailOutcomeId.split(':')[2]} {selectedDetailOutcomeId.split(':')[1]}
						</h3>

						<span style={{ position: 'relative' }}>
							<Button
								onClick={() => {
									setIsDetailView(false);
								}}
								variant="text"
								startIcon={<ArrowBack />}
								style={{ top: 0, right: 0, color: 'white', textTransform: 'none', display: 'block', marginLeft: 'auto' }}>
								Back
							</Button>
						</span>
						<div>
							<TableContainer
								component={Paper}
								className="screener-table-wrap"
								sx={{ maxHeight: '95vh', overflowX: 'auto', overflowY: 'auto', marginTop: 0 }}>
								<Table stickyHeader className="screener-table">
									<TableHead>
										<TableRow>
											<TableCell className="screener-header-sticky" sx={{ position: 'sticky', left: 0, top: 0, zIndex: 10, minWidth: 220, maxWidth: 220 }}>Bet</TableCell>
											<TableCell>{/* line */}</TableCell>
											<TableCell className="screener-col-best">BEST</TableCell>
											<TableCell className="screener-col-average">AVERAGE</TableCell>
											{booksToShow?.map((uniqueBook) => (
												<TableCell key={uniqueBook}>
													<LogoGenerator sportsbook={uniqueBook} />
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedDetailData?.map((od, index) => {
											return Object.entries(od.data)
												.sort(([keyA, _], [keyB]) => {
													const numA = parseFloat(keyA.split(':')[3]);
													const numB = parseFloat(keyB.split(':')[3]);
													if (!isNaN(numA) && !isNaN(numB)) {
														return numA - numB;
													}
													return 0;
												})
												.map(([key, value], dataIndex) => {
													const splitKey = key.split(':');
													const lineSpreadNum = parseFloat(splitKey[3]);
													let priceMap;
													let pricesToUse = (od.data[key] || []).filter((p) => !isExcludedScreenerBook(p?.sportsbook));
													let isHomeTeamNegativeSpread = false;

													try {
														priceMap = mergePricesByDisplaySportsbook(pricesToUse);
													} catch (e) {}
													const betStats = getBetPriceStats(pricesToUse);

													if (dataIndex === 0) {
														return (
															<>
																<TableRow className="screener-game-row">
																	<TableCell
																		className="screener-game-row-sticky"
																		sx={{
																			position: 'sticky',
																			left: 0,
																			top: headerHeight,
																			minWidth: 220,
																			maxWidth: 220,
																			backgroundColor: '#1c1f26',
																			borderBottom: '3px solid var(--screener-border-strong)',
																			color: 'var(--screener-text)',
																			zIndex: 3,
																			boxShadow: '2px 0 6px rgba(0,0,0,0.4)',
																		}}>
																		{od?.game?.away_team_display} @ {od?.game?.home_team_display}
																		<div className="screener-game-meta">{formatCompactDateTime(od?.game?.start_date)}</div>
																	</TableCell>
																	<TableCell
																		className="screener-game-row-spacer"
																		sx={{
																			position: 'sticky',
																			top: headerHeight,
																			zIndex: 2,
																			padding: 0,
																			border: 0,
																			backgroundColor: '#1c1f26',
																		}}
																		colSpan={(booksToShow?.length ?? 0) + 4}
																	/>
																</TableRow>
																<TableRow>
																	<TableCell
																		className="screener-cell-sticky"
																		sx={{
																			position: 'sticky',
																			left: 0,
																			zIndex: 2,
																			minWidth: 220,
																			maxWidth: 220,
																		}}>
																		{splitKey[2]} {splitKey[3]} {splitKey[1]}
																	</TableCell>
																	<TableCell style={{ color: 'white' }} />
																	<TableCell style={{ color: 'white' }}>
																		<Table>
																			{filters?.markets === 'Moneyline' && (
																				<TableBody>
																					<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_over))}</TableRow>
																				</TableBody>
																			)}
																																						{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
												<TableBody>
													<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_over))}</TableRow>
													<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_under))}</TableRow>
												</TableBody>
											)}
											{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																				<TableBody>
																					<TableRow>
																						{addPlusSign(spreadHighestFromStats(betStats, 'over', lineSpreadNum))}
																					</TableRow>
																				</TableBody>
																			)}
																		</Table>
																	</TableCell>
																	<TableCell style={{ color: 'white' }}>
																		<Table>
																			{filters?.markets === 'Moneyline' && (
																				<TableBody>
																					<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
																				</TableBody>
																			)}
																																						{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
												<TableBody>
													<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
													<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_under))}</span></TableCell></TableRow>
												</TableBody>
											)}
											{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																				<TableBody>
																					<TableRow>
																						<span className="screener-average-cell">
																							{addPlusSign(spreadAverageFromStats(betStats, 'over', lineSpreadNum))}
																						</span>
																					</TableRow>
																				</TableBody>
																			)}
																		</Table>
																	</TableCell>

																	{booksToShow?.map((book) => {
																		const rawOver = resolveDeepLink(priceMap?.[book]?.deep_link_over);
																		const rawUnder = resolveDeepLink(priceMap?.[book]?.deep_link_under);
																		let link_over = rawOver ? (rawOver.includes('betmgm') ? rawOver.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawOver.replace(/<STATE>./g, '')) : '';
																		let link_under = rawUnder ? (rawUnder.includes('betmgm') ? rawUnder.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawUnder.replace(/<STATE>./g, '')) : '';
																		let spreadPriceHome = addPlusSign(
																				spreadAmericanFromPriceRow(priceMap?.[book], 'over', lineSpreadNum)
																			);
																			let spreadPriceAway = addPlusSign(
																				spreadAmericanFromPriceRow(priceMap?.[book], 'over', lineSpreadNum)
																			);

																			return (
																				<TableCell sx={{ color: 'white' }} key={book} className="screener-col-book">
																					<Popover
																						onClose={(e) => {
																							setSelectedBet();
																						}}
																						PaperProps={{
																							className: 'screener-popover-paper',
																							sx: { p: 1 },
																						}}
																						anchorOrigin={{ vertical: 'bottom' }}
																						anchorEl={betActionAnchorEl}
																						open={selectedBet?.outcomeId && (selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_over || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_under)}>
																						<BetActionsOverlay
																							linkOver={link_over}
																							linkUnder={link_under}
																							betPriceOver={
																								(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap')
																									? spreadPriceHome
																									: addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																							}
																							betPriceUnder={
																								(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap')
																									? spreadPriceAway
																									: addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																							}
																							sportsbook={book}
																							isSpread={key.market === 'Point Spread' || key.market === 'Asian Handicap'}
																							isHomeTeamNegativeSpread={isHomeTeamNegativeSpread}
																							outcome_id_normalized={priceMap?.[book]?.outcome_id_normalized}
																							sport={priceMap?.[book]?.sport}
																							sportsbooks={uniqueBooks}
																						/>
																					</Popover>
																					<Table>
																						{filters?.markets === 'Moneyline' && (
																							<TableBody>
																								<TableRow
																									onClick={(e) => {
																										setBetActionAnchorEl(e.currentTarget);
																										handleBetClick({
																											type: 'over',
																											book,
																											outcomeId: priceMap?.[book]?.outcome_id_normalized_over ?? priceMap?.[book]?.outcome_id_normalized,
																										});
																									}}
																									sx={{ cursor: 'pointer' }}>
																									<div
																										style={{
																											border: priceMap?.[book]?.bet_price_over ? '1px solid grey' : 'none',
																											borderRadius: 8,
																											textAlign: 'center',
																											marginBottom: 4,
																											minHeight: '24px',
																										}}>
																										{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																											? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																											: NO_ODDS_PLACEHOLDER}
																									</div>
																								</TableRow>
																							</TableBody>
																						)}
																						{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
																							<TableBody>
																								<TableRow
																									onClick={(e) => {
																										setBetActionAnchorEl(e.currentTarget);
																										handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																									}}
																									sx={{ cursor: 'pointer' }}>
																									<div
																										style={{
																											border: priceMap?.[book]?.bet_price_over ? '1px solid grey' : 'none',
																											borderRadius: 8,
																											textAlign: 'center',
																											marginBottom: 4,
																											minHeight: '24px',
																										}}>
																										{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																											? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																											: NO_ODDS_PLACEHOLDER}
																									</div>
																								</TableRow>
																								<TableRow
																									onClick={(e) => {
																										setBetActionAnchorEl(e.currentTarget);
																										handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																									}}
																									sx={{ cursor: 'pointer' }}>
																									<div
																										style={{
																											border: priceMap?.[book]?.bet_price_under ? '1px solid grey' : 'none',
																											borderRadius: 8,
																											textAlign: 'center',
																											minHeight: '24px',
																										}}>
																										{formatAmerican(priceMap?.[book]?.bet_price_under) != null
																											? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																											: NO_ODDS_PLACEHOLDER}
																									</div>
																								</TableRow>
																							</TableBody>
																						)}
																						{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																							<TableBody>
																								{index === 0 ? (
																									<TableRow
																										onClick={(e) => {
																											setBetActionAnchorEl(e.currentTarget);
																											handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																										}}
																										sx={{ cursor: 'pointer' }}>
																										<div
																											style={{
																												border: !isNaN(spreadPriceHome) ? '1px solid grey' : 'none',
																												borderRadius: 8,
																												textAlign: 'center',
																												marginBottom: 4,
																												minHeight: '24px',
																											}}>
																											{!isNaN(spreadPriceHome) ? spreadPriceHome : NO_ODDS_PLACEHOLDER}
																										</div>
																									</TableRow>
																								) : (
																									<TableRow
																										onClick={(e) => {
																											setBetActionAnchorEl(e.currentTarget);
																											handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																										}}
																										sx={{ cursor: 'pointer' }}>
																										<div
																											style={{
																												border: !isNaN(spreadPriceAway) ? '1px solid grey' : 'none',
																												borderRadius: 8,
																												textAlign: 'center',
																												minHeight: '24px',
																											}}>
																											{!isNaN(spreadPriceAway) ? spreadPriceAway : NO_ODDS_PLACEHOLDER}
																										</div>
																									</TableRow>
																								)}
																							</TableBody>
																						)}
																					</Table>
																				</TableCell>
																			);
																	})}
																</TableRow>
															</>
														);
													} else
														return (
															<TableRow>
																<TableCell style={{ color: 'white' }}>{splitKey[2]} {splitKey[3]} {splitKey[1]}</TableCell>
																<TableCell style={{ color: 'white' }} />
																<TableCell style={{ color: 'white' }}>
																	<Table>
																		{filters?.markets === 'Moneyline' && (
																			<TableBody>
																				<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_over))}</TableRow>
																			</TableBody>
																		)}
																																				{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
											<TableBody>
												<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_over))}</TableRow>
												<TableRow>{addPlusSign(formatAmerican(betStats.highest_bet_price_under))}</TableRow>
											</TableBody>
										)}
										{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																			<TableBody>
																				<TableRow>
																					{addPlusSign(spreadHighestFromStats(betStats, 'over', lineSpreadNum))}
																				</TableRow>
																			</TableBody>
																		)}
																	</Table>
																</TableCell>
																<TableCell style={{ color: 'white' }}>
																	<Table>
																		{filters?.markets === 'Moneyline' && (
																			<TableBody>
																				<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
																			</TableBody>
																		)}
																																				{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
											<TableBody>
												<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_over))}</span></TableCell></TableRow>
												<TableRow><TableCell component="td"><span className="screener-average-cell">{addPlusSign(formatAmerican(betStats.average_bet_price_under))}</span></TableCell></TableRow>
											</TableBody>
										)}
										{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																			<TableBody>
																				<TableRow>
																					<span className="screener-average-cell">
																						{addPlusSign(spreadAverageFromStats(betStats, 'over', lineSpreadNum))}
																					</span>
																				</TableRow>
																			</TableBody>
																		)}
																	</Table>
																</TableCell>

																{booksToShow?.map((book) => {
																	const rawOver = resolveDeepLink(priceMap?.[book]?.deep_link_over);
																	const rawUnder = resolveDeepLink(priceMap?.[book]?.deep_link_under);
																	let link_over = rawOver ? (rawOver.includes('betmgm') ? rawOver.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawOver.replace(/<STATE>./g, '')) : '';
																	let link_under = rawUnder ? (rawUnder.includes('betmgm') ? rawUnder.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca') : rawUnder.replace(/<STATE>./g, '')) : '';
																	let spreadPriceHome = addPlusSign(
																			spreadAmericanFromPriceRow(priceMap?.[book], 'over', lineSpreadNum)
																		);
																		let spreadPriceAway = addPlusSign(
																			spreadAmericanFromPriceRow(priceMap?.[book], 'over', lineSpreadNum)
																		);

																		return (
																			<TableCell sx={{ color: 'white' }} key={book} className="screener-col-book">
																				<Popover
																					onClose={(e) => {
																						setSelectedBet();
																					}}
																					PaperProps={{
																						className: 'screener-popover-paper',
																						sx: { p: 1 },
																					}}
																					anchorOrigin={{ vertical: 'bottom' }}
																					anchorEl={betActionAnchorEl}
																					open={selectedBet?.outcomeId && (selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_over || selectedBet?.outcomeId === priceMap?.[book]?.outcome_id_normalized_under)}>
																					<BetActionsOverlay
																						linkOver={link_over}
																						linkUnder={link_under}
																						betPriceOver={
																							(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap')
																								? spreadPriceHome
																								: addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																						}
																						betPriceUnder={
																							(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap')
																								? spreadPriceAway
																								: addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																						}
																						sportsbook={book}
																						isSpread={key.market === 'Point Spread' || key.market === 'Asian Handicap'}
																						isHomeTeamNegativeSpread={isHomeTeamNegativeSpread}
																						outcome_id_normalized={priceMap?.[book]?.outcome_id_normalized}
																						sport={priceMap?.[book]?.sport}
																						sportsbooks={uniqueBooks}
																					/>
																				</Popover>
																				<Table>
																					{filters?.markets === 'Moneyline' && (
																						<TableBody>
																							<TableRow
																								onClick={(e) => {
																									setBetActionAnchorEl(e.currentTarget);
																									handleBetClick({
																										type: 'over',
																										book,
																										outcomeId: priceMap?.[book]?.outcome_id_normalized_over ?? priceMap?.[book]?.outcome_id_normalized,
																									});
																								}}
																								sx={{ cursor: 'pointer' }}>
																								<div
																									style={{
																										border: priceMap?.[book]?.bet_price_over ? '1px solid grey' : 'none',
																										borderRadius: 8,
																										textAlign: 'center',
																										marginBottom: 4,
																										minHeight: '24px',
																									}}>
																									{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																										? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																										: NO_ODDS_PLACEHOLDER}
																								</div>
																							</TableRow>
																						</TableBody>
																					)}
																					{filters?.markets !== 'Moneyline' && filters?.markets !== 'Point Spread' && filters?.markets !== 'Asian Handicap' && (
																						<TableBody>
																							<TableRow
																								onClick={(e) => {
																									setBetActionAnchorEl(e.currentTarget);
																									handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																								}}
																								sx={{ cursor: 'pointer' }}>
																								<div
																									style={{
																										border: priceMap?.[book]?.bet_price_over ? '1px solid grey' : 'none',
																										borderRadius: 8,
																										textAlign: 'center',
																										marginBottom: 4,
																										minHeight: '24px',
																									}}>
																									{formatAmerican(priceMap?.[book]?.bet_price_over) != null
																										? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_over))
																										: NO_ODDS_PLACEHOLDER}
																								</div>
																							</TableRow>
																							<TableRow
																								onClick={(e) => {
																									setBetActionAnchorEl(e.currentTarget);
																									handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																								}}
																								sx={{ cursor: 'pointer' }}>
																								<div
																									style={{
																										border: priceMap?.[book]?.bet_price_under ? '1px solid grey' : 'none',
																										borderRadius: 8,
																										textAlign: 'center',
																										minHeight: '24px',
																									}}>
																									{formatAmerican(priceMap?.[book]?.bet_price_under) != null
																										? addPlusSign(formatAmerican(priceMap?.[book]?.bet_price_under))
																										: NO_ODDS_PLACEHOLDER}
																								</div>
																							</TableRow>
																						</TableBody>
																					)}
																					{(filters?.markets === 'Point Spread' || filters?.markets === 'Asian Handicap') && (
																						<TableBody>
																							{index === 0 ? (
																								<TableRow
																									onClick={(e) => {
																										setBetActionAnchorEl(e.currentTarget);
																										handleBetClick({ type: 'over', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																									}}
																									sx={{ cursor: 'pointer' }}>
																									<div
																										style={{
																											border: !isNaN(spreadPriceHome) ? '1px solid grey' : 'none',
																											borderRadius: 8,
																											textAlign: 'center',
																											marginBottom: 4,
																											minHeight: '24px',
																										}}>
																										{!isNaN(spreadPriceHome) ? spreadPriceHome : NO_ODDS_PLACEHOLDER}
																									</div>
																								</TableRow>
																							) : (
																								<TableRow
																									onClick={(e) => {
																										setBetActionAnchorEl(e.currentTarget);
																										handleBetClick({ type: 'under', book, outcomeId: priceMap?.[book]?.outcome_id_normalized });
																									}}
																									sx={{ cursor: 'pointer' }}>
																									<div
																										style={{
																											border: !isNaN(spreadPriceAway) ? '1px solid grey' : 'none',
																											borderRadius: 8,
																											textAlign: 'center',
																											minHeight: '24px',
																										}}>
																										{!isNaN(spreadPriceAway) ? spreadPriceAway : NO_ODDS_PLACEHOLDER}
																									</div>
																								</TableRow>
																							)}
																						</TableBody>
																					)}
																				</Table>
																			</TableCell>
																		);
																})}
															</TableRow>
														);
												});
										})}
									</TableBody>
								</Table>
							</TableContainer>
						</div>
					</div>
				)}

				{isLoading && (
					<div className="screener-loading">
						<Paper elevation={0} className="screener-loading-card">
							<CircularProgress size={24} thickness={4} />
							<Typography variant="body2" className="screener-loading-text">
								Loading odds…
							</Typography>
						</Paper>
					</div>
				)}
			</div>
		</div>
	);
};

